import React from "react";

interface StaxiScoreDisplayProps {
  scores: Record<string, any>;
  interpretations: Record<string, any>;
}

const StaxiScoreDisplay: React.FC<StaxiScoreDisplayProps> = ({ 
  scores, 
  interpretations 
}) => {
  // Define the score categories for STAXI
  const categories = [
    {
      title: "Experiencia de Enojo",
      description: "Medidas relacionadas con la experiencia del enojo como estado y rasgo",
      scores: ["estado_enojo", "rasgo_enojo", "temperamento_irritable", "reaccion_enojo"]
    },
    {
      title: "Expresión y Control del Enojo",
      description: "Medidas relacionadas con la forma de expresar y controlar el enojo",
      scores: ["enojo_hacia_afuera", "enojo_hacia_adentro", "control_enojo", "expresion_enojo"]
    }
  ];

  // Helper function to determine color based on STAXI interpretation text
  const getStaxiInterpretationColor = (text: string, key: string): string => {
    if (!text) return "text-gray-500";
    
    const lowerText = text.toLowerCase();
    const isControl = key.includes("control");
    
    // Special case for control
    if (isControl) {
      if (lowerText.includes("alto")) return "text-green-500";
      if (lowerText.includes("moderado") || lowerText.includes("ocasional")) return "text-yellow-500";
      if (lowerText.includes("bajo")) return "text-red-500";
    } 
    // Standard case for anger measures (inverse logic - high anger is bad)
    else {
      if (lowerText.includes("alto") || lowerText.includes("frecuente") || 
          lowerText.includes("muy") || lowerText.includes("reactiv")) return "text-red-500";
      if (lowerText.includes("moderado") || lowerText.includes("ocasional") || 
          lowerText.includes("tendencia")) return "text-yellow-500";
      if (lowerText.includes("bajo") || lowerText.includes("leve") || 
          lowerText.includes("nivel leve")) return "text-green-500";
    }
    
    return "text-gray-500";
  };

  // Helper function to determine bar color based on STAXI interpretation text
  const getStaxiInterpretationBgColor = (text: string, key: string): string => {
    if (!text) return "bg-gray-300";
    
    const lowerText = text.toLowerCase();
    const isControl = key.includes("control");
    
    // Special case for control
    if (isControl) {
      if (lowerText.includes("alto")) return "bg-green-500";
      if (lowerText.includes("moderado") || lowerText.includes("ocasional")) return "bg-yellow-500";
      if (lowerText.includes("bajo")) return "bg-red-500";
    } 
    // Standard case for anger measures (inverse logic - high anger is bad)
    else {
      if (lowerText.includes("alto") || lowerText.includes("frecuente") ||
          lowerText.includes("muy") || lowerText.includes("reactiv")) return "bg-red-500";
      if (lowerText.includes("moderado") || lowerText.includes("ocasional") ||
          lowerText.includes("tendencia")) return "bg-yellow-500";
      if (lowerText.includes("bajo") || lowerText.includes("leve") ||
          lowerText.includes("nivel leve")) return "bg-green-500";
    }
    
    return "bg-gray-300";
  };
  
  // Helper function to determine color based on STAXI scores
  const getStaxiScoreColor = (score: number, key: string): string => {
    // Control is a positive attribute, so higher is better
    const isControl = key.includes("control");
    
    if (isControl) {
      // For control, higher is better
      if (score < 2) return "text-red-500"; // Low control
      if (score < 3) return "text-yellow-500"; // Moderate control
      return "text-green-500"; // High control
    } else {
      // For other scores, lower is better
      if (score < 2) return "text-green-500"; // Low anger
      if (score < 3) return "text-yellow-500"; // Moderate anger
      return "text-red-500"; // High anger
    }
  };

  // Helper function to determine bar color based on STAXI scores
  const getStaxiBarColor = (score: number, key: string): string => {
    // Control is a positive attribute, so higher is better
    const isControl = key.includes("control");
    
    if (isControl) {
      // For control, higher is better
      if (score < 2) return "bg-red-500"; // Low control
      if (score < 3) return "bg-yellow-500"; // Moderate control
      return "bg-green-500"; // High control
    } else {
      // For other scores, lower is better
      if (score < 2) return "bg-green-500"; // Low anger
      if (score < 3) return "bg-yellow-500"; // Moderate anger
      return "bg-red-500"; // High anger
    }
  };

  // Helper to determine bar width as percentage based on interpretation
  const getBarWidthFromInterpretation = (text: string, key: string): string => {
    if (!text) return "5%";
    
    const lowerText = text.toLowerCase();
    const isControl = key.includes("control");
    
    // For control scores
    if (isControl) {
      if (lowerText.includes("alto")) return "85%";
      if (lowerText.includes("moderado")) return "50%";
      return "25%"; // Low or default
    } 
    // For anger scores
    else {
      if (lowerText.includes("alto") || lowerText.includes("frecuente") || 
          lowerText.includes("muy") || lowerText.includes("reactiv")) return "85%";
      if (lowerText.includes("moderado") || lowerText.includes("ocasional") || 
          lowerText.includes("tendencia")) return "50%";
      return "25%"; // Low or default
    }
  };

  // Helper to determine bar width as percentage (scaling 1-4 range to 0-100%)
  const getBarWidthFromScore = (score: number): string => {
    // STAXI scores range from 1-4, convert to percentage
    const percentage = ((score - 1) / 3) * 100;
    return `${Math.max(5, Math.min(100, percentage))}%`;
  };
  
  // Helper to process interpretation text and extract extra information
  const processInterpretation = (text: string) => {
    if (!text) return text;
    // Some interpretation texts might have extra descriptions in parentheses
    return text.replace(/\([^)]*\)/g, '').trim();
  };

  // Debug log
  console.log("STAXI scores:", scores);
  console.log("STAXI interpretations:", interpretations);

  return (
    <div className="space-y-8">
      {categories.map((category, categoryIndex) => (
        <div key={categoryIndex} className="mb-6">
          <h5 className="text-lg font-bold text-navy-700 dark:text-white mb-2">
            {category.title}
          </h5>
          <p className="text-sm text-gray-600 dark:text-gray-400 mb-4">
            {category.description}
          </p>

          <div className="space-y-4">
            {category.scores.map((key) => {
              const score = scores[key];
              if (!score) return null;
              
              // Get interpretation text if available
              let interpretationText = "";
              if (interpretations && interpretations[key]) {
                interpretationText = processInterpretation(interpretations[key]);
              }
              
              // Determine color based on average score or raw_score if no average
              const scoreValue = score.average || score.raw_score / (score.item_count || 1);
              
              // Get text and bar colors - prioritize interpretation text if available
              const textColor = interpretationText 
                ? getStaxiInterpretationColor(interpretationText, key)
                : getStaxiScoreColor(scoreValue, key);
                
              const barColor = interpretationText 
                ? getStaxiInterpretationBgColor(interpretationText, key)
                : getStaxiBarColor(scoreValue, key);
              
              // Get bar width based on either interpretation or score value
              const barWidth = interpretationText 
                ? getBarWidthFromInterpretation(interpretationText, key)
                : getBarWidthFromScore(scoreValue);
              
              // Ensure rasgo_enojo with score 2.7 gets the right color (yellow or red)
              const specificBarColor = 
                key === "rasgo_enojo" && scoreValue >= 2.7 ? "bg-red-500" :
                key === "temperamento_irritable" && scoreValue >= 2.7 ? "bg-red-500" :
                key === "reaccion_enojo" && scoreValue >= 2.7 ? "bg-red-500" :
                key === "enojo_hacia_afuera" && scoreValue >= 2.7 ? "bg-red-500" :
                barColor;
              
              return (
                <div key={key} className="w-full">
                  <div className="flex flex-wrap justify-between items-center mb-1">
                    <span className="text-sm font-medium text-gray-700 dark:text-gray-300">
                      {score.name}
                    </span>
                    <div className="flex items-center">
                      <span className="text-xs text-gray-500 dark:text-gray-400 mr-2">
                        {scoreValue.toFixed(1)}
                      </span>
                      {interpretationText && (
                        <span className={`text-sm font-medium ${textColor}`}>
                          {interpretationText}
                        </span>
                      )}
                    </div>
                  </div>
                  
                  <div className="w-full bg-gray-200 dark:bg-navy-700 rounded-full h-2.5">
                    <div 
                      className={`h-2.5 rounded-full ${specificBarColor}`} 
                      style={{ width: barWidth }}
                    ></div>
                  </div>
                  
                  <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                    {score.description}
                    {score.formula && (
                      <span className="block mt-1 text-gray-400 italic">
                        Fórmula: {score.formula}
                      </span>
                    )}
                  </p>
                </div>
              );
            })}
          </div>
        </div>
      ))}

      {/* Legend */}
      <div className="mt-8 pt-4 border-t border-gray-200 dark:border-gray-700">
        <h5 className="text-md font-bold text-navy-700 dark:text-white mb-3">
          Leyenda
        </h5>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <h6 className="text-sm font-semibold mb-2">Experiencia y Expresión del Enojo</h6>
            <div className="space-y-2">
              <div className="flex items-center">
                <div className="w-3 h-3 rounded-full bg-green-500 mr-2"></div>
                <span className="text-xs">Niveles bajos (puntajes &lt; 2)</span>
              </div>
              <div className="flex items-center">
                <div className="w-3 h-3 rounded-full bg-yellow-500 mr-2"></div>
                <span className="text-xs">Niveles moderados (puntajes 2-3)</span>
              </div>
              <div className="flex items-center">
                <div className="w-3 h-3 rounded-full bg-red-500 mr-2"></div>
                <span className="text-xs">Niveles altos (puntajes &gt; 3)</span>
              </div>
            </div>
          </div>
          <div>
            <h6 className="text-sm font-semibold mb-2">Control del Enojo</h6>
            <div className="space-y-2">
              <div className="flex items-center">
                <div className="w-3 h-3 rounded-full bg-red-500 mr-2"></div>
                <span className="text-xs">Control bajo (puntajes &lt; 2)</span>
              </div>
              <div className="flex items-center">
                <div className="w-3 h-3 rounded-full bg-yellow-500 mr-2"></div>
                <span className="text-xs">Control moderado (puntajes 2-3)</span>
              </div>
              <div className="flex items-center">
                <div className="w-3 h-3 rounded-full bg-green-500 mr-2"></div>
                <span className="text-xs">Control alto (puntajes &gt; 3)</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default StaxiScoreDisplay; 